<template>
    <v-dialog :value="menuState" :width="width" persistent @input="updateMenuState($event)">
        <template v-slot:activator="{ on }">
            <slot :on="on"/>
        </template>
        <v-card class="dialog-card">
            <v-toolbar flat>
                <v-toolbar-title class="px-2">
                    <slot name="header">
                        <span class="font-weight-medium" v-html="header_text"></span> <!-- WARNING! THIS CAN BE ARBITRARY! -->
                    </slot>
                </v-toolbar-title>
                <v-spacer/>
                <v-btn icon @click="closeDialog()">
                    <v-icon>close</v-icon>
                </v-btn>
            </v-toolbar>
            <v-card-text :class="['dialog-content pb-0', fullContentWidth ? 'pa-0' : '']">
                <v-row>
                    <v-col>
                        <ComboboxDropdown
                            :items="customFields"
                            :selected="getSelectedCustomField().title"
                            name-key="title"
                            value-key="id"
                            show-selected-first
                            :label="$t('Custom fields')"
                            @input="chooseField"
                        />
                    </v-col>
                </v-row>
                <v-radio-group
                    v-if="localState.selectedOption === 'boolean_equals'"
                    :value="getSelectedBooleanChoice()"
                    @change="selectCustomFieldChoice"
                >
                    <v-radio
                        :label="$t('Yes')"
                        value="1"
                        hide-details
                        class="py-0"
                    />
                    <v-radio
                        :label="$t('No')"
                        value="0"
                        hide-details
                        class="py-0"
                    />
                </v-radio-group>
                <v-radio-group v-else :value="localState.selectedOption" @change="selectOption">
                    <template v-for="(option, index) in getSelectionsByType()">
                        <v-row :key="index" class="py-0" no-gutters>
                            <v-col>
                                <v-row class="pb-3" no-gutters>
                                    <v-col>
                                        <v-radio
                                            :label="option.name"
                                            :value="option.value"
                                            hide-details
                                            class="py-0"
                                        />
                                    </v-col>
                                </v-row>
                                <v-row v-if="localState.selectedOption === option.value" class="pt-0 pb-3" no-gutters
                                       align="center">
                                    <template v-if="localState.selectedOption === 'range'">
                                        <NumericRange
                                            :start-from-value="getRangeStartFromValue()"
                                            :end-from-value="getRangeEndFromValue()"
                                            @updateStartOfRange="updateStartOfRange"
                                            @updateEndOfRange="updateEndOfRange"
                                        />
                                    </template>
                                    <template v-else-if="localState.selectedOption === 'date_range'">
                                        <DateRange
                                            :start-from-value="getDateRangeStartFromValue()"
                                            :end-from-value="getDateRangeEndFromValue()"
                                            @updateStartOfRange="updateDateStartOfRange"
                                            @updateEndOfRange="updateDateEndOfRange"
                                        />

                                    </template>
                                    <template
                                        v-else-if="['boolean_equals', 'list_contains', 'number_list_contains'].includes(localState.selectedOption)">
                                        <v-col cols="8" offset="1">
                                            <ComboboxDropdown
                                                :items="getSelectedCustomFieldChoices()"
                                                :selected="getSelectedChoices()"
                                                name-key="name"
                                                :multi="getIsCustomFieldMulti()"
                                                show-selected-first
                                                solo
                                                dense
                                                :outlined="false"
                                                hide-details
                                                :value-key="getDropDownValueKey()"
                                                @input="selectCustomFieldChoice"
                                            />
                                        </v-col>
                                    </template>
                                    <template v-else-if="['empty'].includes(localState.selectedOption)"/>
                                    <template
                                        v-else-if="['date_greater_than', 'date_less_than'].includes(localState.selectedOption)">
                                        <v-col offset="1" cols="6">
                                            <date-picker
                                                :value="getSelectedValue()"
                                                :hide-label-block="true"
                                                hide-details="auto"
                                                @input="updateValue"
                                            ></date-picker>
                                        </v-col>
                                    </template>
                                    <template v-else>
                                        <v-col offset="1" cols="6">
                                            <v-text-field
                                                :value="getSelectedValue()"
                                                solo
                                                dense
                                                hide-details="auto"
                                                @input="updateValue"
                                            ></v-text-field>
                                        </v-col>
                                    </template>
                                </v-row>
                            </v-col>
                        </v-row>
                    </template>
                </v-radio-group>
            </v-card-text>
            <v-card-actions v-if="!hideFooter" class="dialog-actions-bar pa-4">
                <v-btn text @click="removeFilter()">
                    {{ $t('Remove') }}
                </v-btn>
                <v-spacer></v-spacer>
                <v-btn text @click="closeDialog()">
                    {{ $t('Cancel') }}
                </v-btn>
                <v-btn class="primary" @click="addFilter">
                    {{ $t('Add') }}
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>

import ComboboxDropdown from "../../components/ComboboxDropdown";
import {$t} from "@/i18n";
import {computed, reactive, watch} from "@vue/composition-api";
import {get} from '@/helpers/get-set'
import NumericRange from "../../components/CustomField/FilterOptions/NumericRange.vue";
import DateRange from "../../components/CustomField/FilterOptions/DateRange.vue";
import {formatDate} from "@/plugins/formatting";

export default {
    name: "CustomFieldFilterDialog",
    components: {
        NumericRange,
        DateRange,
        ComboboxDropdown
    },
    props: {
        menuState: {
            type: Boolean,
            default: false
        },
        value: {
            type: [Array, String],
            default: () => []
        },
        items: {
            type: Array,
            default: () => []
        },
        headerText: {
            type: String,
            default: 'Custom field'
        },
        filterKey: {
            type: String,
            default: ''
        },
        nameKey: {
            type: String,
            default: ''
        },
        valueKey: {
            type: String,
            default: ''
        },
        width: {
            type: Number,
            default: 500
        },
        hideFooter: {
            type: Boolean,
            default: false
        },
        fullContentWidth: {
            type: Boolean,
            default: false
        },
        members: {
            type: Array,
            default: () => []
        }
    },
    setup(props, ctx) {

        const localState = reactive({
            selectedField: null,
            selectedOption: null,
            selectedValue: '',
        })

        const customFields = computed(() => {
            const filterableCustomFieldTypes = [
                'boolean',
                'integer',
                'number',
                'text',
                'choice_single',
                'choice_multi',
                'user_single',
                'user_multi',
                'date'
            ]
            return props.items.filter(i => filterableCustomFieldTypes.includes(i.type))
        })

        const filteredMembers = computed(() => {
            return props.members.map(x => {
                x.id = String(x.id)

                return x
            })
        })

        const selectionsByType = {
            // Maybe we should fetch these from the backend when modal is opened
            text: [
                {name: $t('Contains'), label_name: $t('contains'), value: 'text_contains'},
                {name: $t('Exactly'), label_name: $t('is exactly'), value: 'text_exactly'}
            ],
            number: [
                {name: $t('Exactly'), label_name: $t('is exactly'), value: 'exactly'},
                {name: $t('Greater than'), label_name: $t('is greater than'), value: 'greater_than'},
                {name: $t('Less than'), label_name: $t('is less than'), value: 'less_than'},
                {name: $t('Range'), label_name: $t('is in range of'), value: 'range'}
            ],
            integer: [
                {name: $t('Exactly'), label_name: $t('is exactly'), value: 'exactly'},
                {name: $t('Greater than'), label_name: $t('is greater than'), value: 'greater_than'},
                {name: $t('Less than'), label_name: $t('is less than'), value: 'less_than'},
                {name: $t('Range'), label_name: $t('is in range of'), value: 'range'}
            ],
            date: [
                {name: $t('Greater than'), label_name: $t('is greater than'), value: 'date_greater_than'},
                {name: $t('Less than'), label_name: $t('is less than'), value: 'date_less_than'},
                {name: $t('Range'), label_name: $t('is in range of'), value: 'date_range'},
            ],
            choice_single: [
                {name: $t('is'), label_name: $t('is'), value: 'list_contains'},
            ],
            choice_multi: [
                {name: $t('Contains'), label_name: $t('contains'), value: 'list_contains'}
            ],
            boolean: [
                {name: $t('is'), label_name: '', value: 'boolean_equals'}
            ],
            user_single: [
                {name: $t('Contains'), label_name: $t('Contains'), value: 'number_list_contains'},
                {name: $t('is empty'), label_name: $t('is empty'), value: 'empty'},
            ],
            user_multi: [
                {name: $t('Contains'), label_name: $t('Contains'), value: 'number_list_contains'},
                {name: $t('is empty'), label_name: $t('is empty'), value: 'empty'},
            ]
        }
        const getSelectedCustomFields = function () {
            return props.items.filter(x => x.name === localState.selectedField) || []
        }

        const getSelectedCustomField = function () {
            return getSelectedCustomFields().find(x => x) || {}
        }

        // custom_field = ['client.reg_code', 'exactly', 20]
        const getSelectedCustomFieldName = function () {
            return props.value[0]
        }
        const getSelectedOption = function () {
            return props.value[1]
        }
        const getSelectionsByType = function () {
            let field = getSelectedCustomField()
            return selectionsByType[field.type]
        }
        const getSelectedCustomFieldValue = function () {
            return props.value[2]
        }

        const getDropDownValueKey = () => {
            if (['user_single', 'user_multi'].includes(get(getSelectedCustomField(), 'type'))) {
                return 'id'
            } else {
                return 'code'
            }
        }

        const closeDialog = function () {
            updateMenuState(false)
        };

        const updateMenuState = function (event) {
            ctx.emit('update:menuState', event)
        }

        const chooseField = function (custom_field) {
            localState.selectedValue = null

            if (custom_field.type === 'boolean') {
                localState.selectedOption = 'boolean_equals'
            } else {
                localState.selectedOption = null
            }

            localState.selectedField = custom_field.name
        }

        const getIsCustomFieldMulti = () => {
            let customFieldType = getSelectedCustomField().type
            return ['choice_multi', 'user_multi', 'user_single'].includes(customFieldType)
        }

        const selectOption = function (val) {
            localState.selectedValue = null
            localState.selectedOption = val
        }

        const updateValue = function (value) {
            localState.selectedValue = value
        }

        const getSelectedValue = () => {
            return localState.selectedValue
        }

        const addFilter = function () {
            ctx.emit('input', ([localState.selectedField, localState.selectedOption, localState.selectedValue]))
            ctx.emit('friendly_label', buildFriendlyLabel())
            closeDialog()
        }

        const buildFriendlyLabel = function () {
            let title = getSelectedCustomField().title
            let selections = getSelectionsByType()
            let option = selections ? selections.find(s => s.value === localState.selectedOption) : ''

            let value = localState.selectedValue
            let choiceNames = getSelectedChoicesNames()

            if (["date_range", "date_greater_than", "date_less_than"].includes(option.value)) {
                value = formatDateForFriendlyLabel(value)
            } else if (choiceNames) {
                value = choiceNames
            }

            if (title && option && value) {
                return `${title} ${option.label_name}: ${value}`
            } else {
                return null
            }
        }

        const getSplitValue = function () {
            return localState.selectedValue ? localState.selectedValue.split('-') : []
        }

        const formatDateForFriendlyLabel = (date) => {
            let label = ""
            const dates = date.split('~')

            if (dates.length === 1) {
                label = `${formatDate(dates[0])}`
            } else if (dates.length === 2) {
                label = `${formatDate(dates[0])} - ${formatDate(dates[1])}`
            }

            return label
        }

        const getDateRangeStartFromValue = () => {
            return localState.selectedValue ? localState.selectedValue.split('~')[0] : null
        }

        const getDateRangeEndFromValue = () => {
            return localState.selectedValue ? localState.selectedValue.split('~')[1] : null
        }

        const updateDateStartOfRange = (val) => {
            const selectedValue = localState.selectedValue ? localState.selectedValue.split('~') : []
            selectedValue[0] = val
            localState.selectedValue = selectedValue.join('~')
        }

        const updateDateEndOfRange = (val) => {
            const selectedValue = localState.selectedValue ? localState.selectedValue.split('~') : []
            selectedValue[1] = val
            localState.selectedValue = selectedValue.join('~')
        }

        const getRangeStartFromValue = function () {
            let split = getSplitValue()
            return split ? split[0] : ''
        }
        const getRangeEndFromValue = function () {
            let split = getSplitValue()
            return split && split.length > 1 ? split[1] : ''
        }
        const updateStartOfRange = function (val) {
            let split = getSplitValue()
            split[0] = val
            localState.selectedValue = split.join('-')
        }
        const updateEndOfRange = function (val) {
            let split = getSplitValue()
            if (split.length < 1) split = ['', '']
            split[1] = val
            localState.selectedValue = split.join('-')
        }
        const removeFilter = function () {
            ctx.emit('remove')
        }

        const getSelectedCustomFieldChoices = function () {
            const booleanChoices = [
                {name: $t('Yes'), code: '1'},
                {name: $t('No'), code: '0'}
            ]
            if (get(getSelectedCustomField(), 'type') === 'boolean') {
                return booleanChoices
            }
            if (['user_single', 'user_multi'].includes(get(getSelectedCustomField(), 'type'))) {
                return filteredMembers.value
            } else {
                return get(getSelectedCustomField(), '_data.choices')
            }
        }

        const getSelectedChoicesNames = function () {
            let choices = getSelectedChoices()
            return choices.map(x => x.name).join(', ')
        }

        const getSelectedChoices = function () {
            let choices = getSelectedCustomFieldChoices()
            let selectedValue = localState.selectedValue ? localState.selectedValue.split('~') : []
            let valueKey = getDropDownValueKey()

            return choices ? choices.filter(x => selectedValue.includes(String(x[valueKey]))) : []
        }

        const getSelectedBooleanChoice = () => {
            return localState.selectedValue
        }

        const selectCustomFieldChoice = function (event) {
            if (Array.isArray(event)) {
                let valueKey = getDropDownValueKey()
                localState.selectedValue = event.map(x => x[valueKey]).join('~')

            } else if (typeof event === 'string') {
                localState.selectedValue = event
            } else {
                let valueKey = getDropDownValueKey()
                let value = event[valueKey]

                localState.selectedValue = String(value)
            }
        }

        watch (() => props.value, () => {
            localState.selectedField = getSelectedCustomFieldName()
            localState.selectedOption = getSelectedOption()
            localState.selectedValue = getSelectedCustomFieldValue()
            // if value changes programmatically, label should change as well
            ctx.emit('friendly_label', buildFriendlyLabel())
        }, { immediate: true })

        watch (() => props.items, () => {
            // items need to be available, otherwise we dont know the field title
            ctx.emit('friendly_label', buildFriendlyLabel())
        }, { immediate: true })

        return {
            updateMenuState,
            header_text: props.headerText,
            closeDialog,
            selectionsByType,
            getSelectionsByType,
            chooseField,
            customFields,
            selectOption,
            updateValue,
            localState,
            addFilter,
            getRangeStartFromValue,
            getRangeEndFromValue,
            updateStartOfRange,
            updateEndOfRange,
            removeFilter,
            getSelectedCustomField,
            getSelectedCustomFieldChoices,
            selectCustomFieldChoice,
            getSelectedChoices,
            getSelectedBooleanChoice,
            getDropDownValueKey,
            getIsCustomFieldMulti,
            getSelectedValue,
            getDateRangeStartFromValue,
            getDateRangeEndFromValue,
            updateDateStartOfRange,
            updateDateEndOfRange
        };
    }
};
</script>
